/**
 * `effect-layout/1` — the spatial map the audio-reactive engine renders into.
 *
 * The controller and the Python sidecar both recompute the layout's digest before a single frame is
 * rendered (`audio-reactive/PROTOCOL.md`, `internal/live/effect/audio_layout.go`), and the daemon
 * rejects a document whose digest does not match. The console therefore recomputes it too: a truncated
 * download or a hand-edited file is caught here, in a sentence a person can act on, instead of coming
 * back from the Pi as a protocol complaint after the operator pressed Start.
 *
 * Array order is load-bearing in exactly one place: `elements` IS the order of floats in every
 * `audio-effect/1` frame, so element order is hashed as given. Zones and their tags are sets for
 * rendering purposes, so they are canonicalised before hashing — a rig build that emits its zones in a
 * different order produces the same digest.
 *
 * **Every string this module returns is staff "Details" text, never operator copy.** Naming the artifact,
 * the rig version and the checksum is these sentences' whole job: they are rendered only by
 * `TechnicalDetails`, beside the one sentence `effectUnavailableMessage` gives the operator. The copy law
 * lists this file in `DETAIL_ONLY_FILES` for exactly that reason (`operatorCopy.test.ts`).
 */

import type { EffectLayoutArtifact } from './types';

export const EFFECT_LAYOUT_SCHEMA = 'effect-layout/1';
export const EFFECT_LAYOUT_SCHEMAS = new Set(['effect-layout/1', 'effect-layout/2']);

/** A verified layout plus the element count the rig version recorded beside it. */
export interface LoadedEffectLayout {
  layout: EffectLayout;
  elementCount?: number;
}

/**
 * Why an effect panel can or cannot start, as ONE value, so "not fetched yet" can never be mistaken for
 * "this room has none" — the confusion that made three panels claim an equipped room was unequipped.
 *
 * - `loading` — a read is in flight, or the room's rig has not come back yet. Say nothing; disable Start.
 * - `ready` — `effectLayout()` holds a verified layout for the rig the controller has loaded.
 * - `unpublished` — the active rig version genuinely carries no effect map.
 * - `unavailable` — there is one, and this console cannot get it (download refused, damaged, or no path
 *   to it at all, as on the controller-served console).
 *
 * The last three all read the same to an operator; only `detail` differs, and only staff see that.
 */
export type EffectLayoutStatus = 'loading' | 'ready' | 'unpublished' | 'unavailable';

/**
 * The controller's own console is served off the Pi over a cable, with no cloud session behind it. The
 * daemon carries the artifact's METADATA in its loaded rig version but never fetches the document and
 * exposes no route that would serve it (`internal/server/server.go` mounts the command socket and the
 * static console, nothing else), and `get_rig` projects metadata but not the bytes. So there is no source
 * for this document on a direct connection, and saying so plainly beats implying the rig is at fault.
 */
export const DIRECT_CONSOLE_STAFF_DETAIL = 'This console is served by the controller over a direct connection. The daemon holds no effect-layout/1 document and exposes no route that serves one, so the artifact can only be downloaded by a console signed in to the platform.';

/** The honest reason when the room's active rig version simply carries no `effect-layout/1` artifact. */
export function unpublishedEffectLayoutStaffDetail(rigVersionId: string | null | undefined): string {
  const version = String(rigVersionId ?? '').trim();
  return version
    ? `No effect-layout/1 artifact is published with rig version ${version}.`
    : 'No effect-layout/1 artifact is published with the room\'s active rig version.';
}

/**
 * Either there are bytes to fetch, or there is a settled verdict. Narrow with `'artifact' in source`.
 *
 * `key` is the layout's IDENTITY — the /2 content digest or the /1 rig version, plus the checksum of
 * the bytes. It is deliberately not the URL: the artifact's URL is a short-lived signed one re-minted on every rig
 * read, so keying on it would re-download the same document endlessly and leave a hole each time.
 */
export type EffectLayoutSource =
  | { artifact: EffectLayoutArtifact; key: string }
  | { status: Exclude<EffectLayoutStatus, 'ready'>; detail: string };

/**
 * What the console should do about the room's effect geometry, given the room rig it currently holds.
 *
 * Pulled out of the provider so the one decision that broke — treating "no rig read yet" as "this room
 * has none" — is a function with a truth table rather than a branch inside an effect.
 *
 * `hasCloudSession` is false on the controller-served console, where no room rig will ever arrive. There
 * the absence is permanent and is reported as such; everywhere else it means the read is still in flight.
 */
export function resolveEffectLayoutSource(
  rig: { activeRigVersionId?: string | null; effectLayout?: EffectLayoutArtifact | null } | undefined,
  hasCloudSession: boolean,
): EffectLayoutSource {
  if (!rig) {
    return hasCloudSession
      ? { status: 'loading', detail: '' }
      : { status: 'unavailable', detail: DIRECT_CONSOLE_STAFF_DETAIL };
  }
  const artifact = rig.effectLayout;
  if (!artifact?.url) {
    return {
      status: 'unpublished',
      detail: unpublishedEffectLayoutStaffDetail(artifact?.rigVersionId ?? rig.activeRigVersionId),
    };
  }
  const key = artifact.schema === 'effect-layout/2'
    ? `${artifact.layoutDigest ?? ''}:${artifact.sha256 ?? ''}`
    : `${artifact.rigVersionId ?? rig.activeRigVersionId ?? ''}:${artifact.sha256 ?? ''}`;
  return { artifact, key };
}

/** Unknown controller state is not a mismatch; once both content digests exist they must agree exactly. */
export function effectMapIsStale(mapLayoutDigest: unknown, loadedLayoutDigest: unknown): boolean {
  const map = typeof mapLayoutDigest === 'string' ? mapLayoutDigest.trim() : '';
  const loaded = typeof loadedLayoutDigest === 'string' ? loadedLayoutDigest.trim() : '';
  return Boolean(map && loaded && map !== loaded);
}

/**
 * The controller's refusals of an effect map that is not its loaded layout: an effect-layout/1
 * `rigVersionId` mismatch, an effect-layout/2 digest mismatch, and the older rig-version wordings. ONE
 * pattern, read by `isEffectMapRigMismatchError` (the card reloads the room rig) and by `plainErrors`
 * (the operator reads "Room updated"), so the reload and the words can never disagree (CONTRACT-console S38).
 */
export const EFFECT_MAP_RIG_MISMATCH = /map .*older rig version|rig version .*does not match|effect layout .*rigVersionId.*does not match loaded rig|effect layout digest .*differs from loaded version/i;

/** The controller's refusal of an effect-layout/2 Start when its loaded rig carries no effect layout. */
export const EFFECT_MAP_NOT_LOADED = /loaded rig has no effect-layout\/2 binding/i;

export function isEffectMapRigMismatchError(error: unknown): boolean {
  const raw = typeof error === 'string'
    ? error
    : String((error as { message?: unknown } | null)?.message ?? error ?? '');
  return EFFECT_MAP_RIG_MISMATCH.test(raw);
}

export interface EffectLayoutVector {
  x: number;
  y: number;
  z: number;
}

export interface EffectLayoutZone {
  name: string;
  tags: string[];
  order: number;
}

export interface EffectLayoutElement {
  deviceId: string;
  elementIndex: number;
  /** Room position in z-up metres. */
  world: EffectLayoutVector;
  /** Position within the element's own zone; every component is 0..1. */
  zonePosition: EffectLayoutVector;
  zone: string;
}

export interface EffectLayout {
  schema: string;
  rigVersionId?: string;
  layoutDigest: string;
  zones: EffectLayoutZone[];
  elements: EffectLayoutElement[];
}

const UTF8 = new TextEncoder();

/**
 * Go's `sort.Strings` orders by UTF-8 BYTES; JavaScript's default `Array.prototype.sort` orders by
 * UTF-16 code units. The two disagree above the BMP, which would silently produce a different digest
 * for a layout with non-ASCII zone names, so the comparison is done on the encoded bytes.
 */
function compareUtf8(left: string, right: string): number {
  const first = UTF8.encode(left);
  const second = UTF8.encode(right);
  const shared = Math.min(first.length, second.length);
  for (let index = 0; index < shared; index += 1) {
    if (first[index] !== second[index]) return first[index] - second[index];
  }
  return first.length - second.length;
}

/** The hash pre-image, assembled in the exact field order the protocol fixes. */
class DigestBuffer {
  private readonly chunks: Uint8Array[] = [];
  private length = 0;

  raw(bytes: Uint8Array): void {
    this.chunks.push(bytes);
    this.length += bytes.length;
  }

  uint32(value: number): void {
    const chunk = new Uint8Array(4);
    new DataView(chunk.buffer).setUint32(0, value >>> 0, false);
    this.raw(chunk);
  }

  int64(value: number): void {
    const chunk = new Uint8Array(8);
    new DataView(chunk.buffer).setBigInt64(0, BigInt(Math.trunc(value)), false);
    this.raw(chunk);
  }

  float64(value: number): void {
    const chunk = new Uint8Array(8);
    new DataView(chunk.buffer).setFloat64(0, value, false);
    this.raw(chunk);
  }

  string(value: string): void {
    const encoded = UTF8.encode(value);
    this.uint32(encoded.length);
    this.raw(encoded);
  }

  take(): Uint8Array<ArrayBuffer> {
    const out = new Uint8Array(new ArrayBuffer(this.length));
    let offset = 0;
    for (const chunk of this.chunks) {
      out.set(chunk, offset);
      offset += chunk.length;
    }
    return out;
  }
}

function hex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** The published digest algorithm, byte for byte. Rendered as `sha256:<64 lowercase hex characters>`. */
export async function effectLayoutDigest(layout: EffectLayout): Promise<string> {
  const buffer = new DigestBuffer();
  buffer.string(layout.schema);
  if (layout.schema === 'effect-layout/1') buffer.string(layout.rigVersionId ?? '');

  const zones = [...layout.zones].sort((left, right) => left.order - right.order || compareUtf8(left.name, right.name));
  buffer.uint32(zones.length);
  for (const zone of zones) {
    buffer.string(zone.name);
    buffer.int64(zone.order);
    const tags = [...zone.tags].sort(compareUtf8);
    buffer.uint32(tags.length);
    for (const tag of tags) buffer.string(tag);
  }

  buffer.uint32(layout.elements.length);
  for (const element of layout.elements) {
    buffer.string(element.deviceId);
    buffer.uint32(element.elementIndex);
    buffer.string(element.zone);
    buffer.float64(element.world.x);
    buffer.float64(element.world.y);
    buffer.float64(element.world.z);
    buffer.float64(element.zonePosition.x);
    buffer.float64(element.zonePosition.y);
    buffer.float64(element.zonePosition.z);
  }

  return `sha256:${hex(await crypto.subtle.digest('SHA-256', buffer.take()))}`;
}

function isVector(value: unknown): value is EffectLayoutVector {
  if (!value || typeof value !== 'object') return false;
  const vector = value as EffectLayoutVector;
  return Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z);
}

function isNormalized(vector: EffectLayoutVector): boolean {
  return [vector.x, vector.y, vector.z].every((component) => component >= 0 && component <= 1);
}

/**
 * Everything the console can check about an effect map on its own, in plain language.
 *
 * Returns the first problem as a sentence an operator can act on, or `undefined` when the document is a
 * well-formed effect layout whose digest it recomputes to. Rig-dependent checks (that the map covers
 * every intensity-bearing element exactly once) can only be made by the daemon, which makes them when
 * the effect starts — so a clean verdict here means "worth sending", not "the Pi will accept it".
 */
export async function validateEffectLayout(value: unknown): Promise<string | undefined> {
  const layout = value as Partial<EffectLayout> | null;
  if (!layout || typeof layout !== 'object') return 'That file is not a room effect map.';
  if (!EFFECT_LAYOUT_SCHEMAS.has(layout.schema ?? '')) return 'That file is not a room effect map.';
  if (layout.schema === 'effect-layout/1' && (typeof layout.rigVersionId !== 'string' || !layout.rigVersionId)) {
    return 'That effect map does not say which version of the rig it was built for.';
  }
  if (layout.schema === 'effect-layout/2' && 'rigVersionId' in layout) return 'That file is not a room effect map.';
  if (typeof layout.layoutDigest !== 'string' || !layout.layoutDigest) {
    return 'That effect map has no checksum, so there is no way to tell whether it is intact.';
  }

  if (!Array.isArray(layout.zones) || layout.zones.length === 0) return 'That effect map names no areas of the room.';
  for (const zone of layout.zones) {
    if (!zone || typeof zone.name !== 'string' || !zone.name) return 'That effect map has an area with no name.';
    if (!Number.isInteger(zone.order)) return `That effect map does not place ${zone.name} in the room's order.`;
    if (!Array.isArray(zone.tags) || zone.tags.length === 0 || zone.tags.some((tag) => typeof tag !== 'string' || !tag)) {
      return `That effect map does not say what kind of place ${zone.name} is.`;
    }
  }

  if (!Array.isArray(layout.elements) || layout.elements.length === 0) return 'That effect map lists no lights.';
  const zoneNames = new Set(layout.zones.map((zone) => zone.name));
  for (const element of layout.elements) {
    if (!element || typeof element.deviceId !== 'string' || !element.deviceId) {
      return 'That effect map has a light that belongs to no fixture.';
    }
    if (!Number.isInteger(element.elementIndex) || element.elementIndex < 0) {
      return 'That effect map numbers one of its lights impossibly.';
    }
    if (!zoneNames.has(element.zone)) return 'That effect map puts a light in an area it never describes.';
    if (!isVector(element.world)) return 'That effect map has a light with no position in the room.';
    if (!isVector(element.zonePosition) || !isNormalized(element.zonePosition)) {
      return 'That effect map places a light outside the area it belongs to.';
    }
  }

  if (await effectLayoutDigest(layout as EffectLayout) !== layout.layoutDigest) {
    return 'That effect map does not match its own checksum, so it has been changed or truncated since the rig built it.';
  }
  return undefined;
}

/**
 * The room's two ends, as the effect map itself names them — the traversal order effects use is
 * `(zone.order, zone.name)`, so the first and last of that order ARE the ends.
 */
export function effectLayoutZoneEnds(layout: EffectLayout): string[] {
  return [...layout.zones]
    .sort((left, right) => left.order - right.order || compareUtf8(left.name, right.name))
    .map((zone) => zone.name);
}

/**
 * A verified layout, or the one sentence that says why the published bytes cannot be used. Narrow with
 * `'problem' in result` — the two cases are mutually exclusive and there is no partial success.
 */
export type PublishedEffectLayoutResult = { layout: EffectLayout } | { problem: string };

/** Plain sha256 of raw bytes, lowercase hex — the form the artifact metadata records. */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  return hex(await crypto.subtle.digest('SHA-256', bytes));
}

/**
 * Download and fully verify the effect map published with a rig version.
 *
 * The bytes are checked twice, against two independent claims the platform made when it attached them
 * (CONTRACT-effect-layout-artifact S1/S5): `sha256` covers the stored FILE, so a truncated or swapped
 * download is caught before the JSON is even parsed; `layoutDigest` covers the document's canonical
 * ENCODING, so a semantically-tampered map is caught after. A /1 document also names the rig version
 * it arrived beside (S2); a /2 document binds by content digest so republishing the same room keeps
 * the same map.
 *
 * Returns the parsed layout, or a `problem` an operator can act on. It never throws for a bad artifact
 * or a failed download; a caller passing a fetch that rejects gets that reported as a problem too.
 */
export async function loadPublishedEffectLayout(
  artifact: EffectLayoutArtifact,
  fetchImpl: typeof fetch = fetch,
): Promise<PublishedEffectLayoutResult> {
  if (!artifact.url) return { problem: 'This rig version published no effect map to download.' };

  let bytes: ArrayBuffer;
  try {
    const response = await fetchImpl(artifact.url);
    if (!response.ok) {
      return { problem: `The effect map published with this rig could not be downloaded (${response.status}).` };
    }
    bytes = await response.arrayBuffer();
  } catch {
    return { problem: 'The effect map published with this rig could not be downloaded.' };
  }

  if (artifact.sizeBytes != null && bytes.byteLength !== artifact.sizeBytes) {
    return { problem: 'The effect map that came back is not the size the rig recorded, so the download is incomplete.' };
  }
  if (artifact.sha256 && await sha256Hex(bytes) !== artifact.sha256.toLowerCase()) {
    return { problem: 'The effect map that came back does not match the checksum the rig recorded, so it arrived damaged.' };
  }

  let document: unknown;
  try {
    document = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
  } catch {
    return { problem: 'The effect map published with this rig is not readable as a room effect map.' };
  }

  const problem = await validateEffectLayout(document);
  if (problem) return { problem };
  const layout = document as EffectLayout;

  if (artifact.layoutDigest && layout.layoutDigest !== artifact.layoutDigest) {
    return { problem: 'The effect map published with this rig does not match the checksum recorded beside it.' };
  }
  if (layout.schema === 'effect-layout/1' && artifact.rigVersionId && layout.rigVersionId !== artifact.rigVersionId) {
    return { problem: 'The effect map published with this rig was built for a different version of the rig.' };
  }
  return { layout };
}
